/**
 * Aspect — SceneStore (single client-side source of truth)
 * --------------------------------------------------------------
 * Owns the canonical room + placement records + catalog mirror on the client.
 * Every consumer (rendering, walk collision, plan editor model, minimap,
 * cutaway) derives from these records via change subscriptions, so meshes and
 * collider footprints can never disagree — they are computed from the SAME
 * data at the SAME time.
 *
 * Plain module: no engine imports, no DOM, fully unit-testable in Node.
 *
 * Events:
 *   - "room"        (room)        the room object was replaced or patched
 *   - "placements"  ()            placement set changed (upsert/remove/clear/in-place)
 *   - "catalog"     (assets[])    catalog mirror replaced
 */

export class SceneStore {
  constructor() {
    /** @type {object|null} canonical room (walls, openings, bounds, floors…) */
    this.room = null;
    /** @type {Map<string, object>} placement id -> record */
    this.placements = new Map();
    /** @type {Map<string, object>} asset_id -> catalog meta */
    this.catalog = new Map();

    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();
  }

  /** Subscribe to a store event. Returns an unsubscribe function. */
  on(event, fn) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(fn);
    return () => this._listeners.get(event)?.delete(fn);
  }

  _emit(event, payload) {
    for (const fn of this._listeners.get(event) ?? []) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[store] ${event} listener failed`, err);
      }
    }
  }

  // --- Room -------------------------------------------------------------

  /** Replace the canonical room object. */
  setRoom(room) {
    this.room = room;
    this._emit("room", room);
  }

  /**
   * Mutate the current room in place (topology/opening/lights patches) and
   * notify subscribers. The mutator receives the live room object.
   */
  patchRoom(mutator) {
    if (!this.room) return;
    mutator(this.room);
    this._emit("room", this.room);
  }

  // --- Placements ---------------------------------------------------------

  upsertPlacement(record) {
    if (!record?.id) return;
    this.placements.set(record.id, record);
    this._emit("placements");
  }

  removePlacement(id) {
    const deleted = this.placements.delete(id);
    if (deleted) this._emit("placements");
    return deleted;
  }

  clearPlacements() {
    if (!this.placements.size) return;
    this.placements.clear();
    this._emit("placements");
  }

  /** Signal in-place mutation of placement records (e.g. gizmo drag writes). */
  touchPlacements() {
    this._emit("placements");
  }

  list() {
    return [...this.placements.values()];
  }

  // --- Catalog --------------------------------------------------------------

  setCatalog(assets) {
    this.catalog.clear();
    for (const a of assets ?? []) {
      if (a?.asset_id) this.catalog.set(a.asset_id, a);
    }
    this._emit("catalog", [...this.catalog.values()]);
  }
}

export default SceneStore;
